import { useMemo } from 'react';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useFundContext from '@/contexts/Fund/useFundContext';
import { ReturnedPaymentDtoIn } from '@/models/in/ReturnedPaymentDtoIn';

/** The fund's deleted accounts that payments bounced from; also the react-query key. */
export const returnedPaymentsUrl = (fundId: number) => `funds/${fundId}/support-requests/returned-payments`;

/** Replaces one deleted account in every live request of the fund on it. */
export const fixReturnedPaymentUrl = (fundId: number, returnedAccountId: number) =>
  `${returnedPaymentsUrl(fundId)}/${returnedAccountId}`;

/** Cancels the fund's returned months on the account - already filed again as new requests. */
export const cancelReturnedPaymentUrl = (fundId: number, returnedAccountId: number) =>
  `${fixReturnedPaymentUrl(fundId, returnedAccountId)}/cancel-returned`;

export interface ReturnedPayments {
  /** One per deleted account, each with the requests that bounced from it. */
  groups: ReturnedPaymentDtoIn[];
  /** False until the server answered - an empty list before that means nothing yet. */
  isLoaded: boolean;
  /** The group a request waits in, if it waits for an account at all. */
  groupOf: (correlationId: string) => ReturnedPaymentDtoIn | undefined;
}

/**
 * HAT-1395: the fund's accounts the office deleted after a payment bounced from them, each with the
 * requests waiting for another one. Read once for the whole fund, not from the page's rows: the
 * rows are one date range, and a returned month is usually an older one. The board, the drawer and
 * the dialog all read the same cached answer.
 *
 * Not behind the feature flag: the list is empty until an office user the flag is on for deletes
 * an account through «תשלום חזר». Gating it as well would let a mail arrive whose link opens nothing.
 */
export default function useReturnedPayments(): ReturnedPayments {
  const { currentFund } = useFundContext();
  const { data } = useGetAuthApi<ReturnedPaymentDtoIn[]>(currentFund ? returnedPaymentsUrl(currentFund.id) : '');

  return useMemo(() => {
    const groups = data ?? [];
    const byRequest = new Map<string, ReturnedPaymentDtoIn>();
    groups.forEach((group) => group.requests.forEach((request) => byRequest.set(request.correlationId, group)));
    return { groups, isLoaded: data !== undefined, groupOf: (correlationId: string) => byRequest.get(correlationId) };
  }, [data]);
}
